import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpClient } from '@angular/common/http';
import { UserCredential } from 'firebase/auth';
import { finalize, from } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TenantInfoService } from '../services/tenant-info.service';
import { ThemeService } from '../services/theme.service';
import enTranslations from '../../assets/i18n/en.json';

function flattenTranslationsObject(value: Record<string, unknown>, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {};
  Object.entries(value).forEach(([key, entry]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      Object.assign(result, flattenTranslationsObject(entry as Record<string, unknown>, path));
    } else if (entry != null) {
      result[path] = String(entry);
    }
  });
  return result;
}

const FALLBACK_TRANSLATIONS = flattenTranslationsObject(enTranslations as Record<string, unknown>);

@Component({
  selector: 'app-sign-in',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatDividerModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignInComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly tenantInfoService = inject(TenantInfoService);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(HttpClient);

  private readonly supportedLanguages = new Set(['en', 'de', 'fr', 'es', 'ja']);
  private readonly defaultLanguage = 'en';
  private readonly currentLanguage = signal(this.defaultLanguage);
  private readonly translations = signal<Record<string, string>>(FALLBACK_TRANSLATIONS);
  private readonly defaultTranslations = signal<Record<string, string>>(FALLBACK_TRANSLATIONS);

  readonly providerToggles = this.authService.providerToggles;
  readonly showThirdPartyProviders = computed(
    () => this.providerToggles().google || this.providerToggles().facebook || this.providerToggles().apple
  );
  readonly isCreatingAccount = signal(false);
  readonly submitButtonTranslationKey = computed(() =>
    this.isCreatingAccount() ? 'actions.submit.signUp' : 'actions.submit.signIn'
  );
  readonly providerSectionTranslationKey = computed(() =>
    this.isCreatingAccount() ? 'actions.providers.sectionSignUp' : 'actions.providers.sectionSignIn'
  );
  readonly modeSwitchLabelTranslationKey = computed(() =>
    this.isCreatingAccount() ? 'actions.modeSwitch.signIn' : 'actions.modeSwitch.signUp'
  );
  readonly modeSwitchPrefixTranslationKey = computed(() =>
    this.isCreatingAccount() ? 'actions.modeSwitch.prefixSignUp' : 'actions.modeSwitch.prefixSignIn'
  );

  readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: this.fb.nonNullable.control({ value: '', disabled: true })
  });

  readonly tenantName = signal<string | null>(null);
  readonly tenantId = signal<string | null>(null);
  readonly redirectUrl = signal<string | null>(null);
  readonly state = signal<string | null>(null);
  readonly loadingTenant = signal(false);
  readonly submitInFlight = signal(false);
  private readonly errorState = signal<
    | { type: 'key'; key: string; params?: Record<string, string | number> }
    | { type: 'message'; message: string }
    | null
  >(null);
  readonly errorMessage = computed(() => {
    const error = this.errorState();
    if (!error) {
      return null;
    }
    return error.type === 'key' ? this.t(error.key, error.params) : error.message;
  });

  constructor() {
    this.themeService.init();
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.applyLanguage(params.get('lang'));
        const tenantIdParam = params.get('tenantId');
        const redirectParam = params.get('redirectUrl');
        const stateParam = params.get('state');
        this.tenantId.set(tenantIdParam);
        this.state.set(stateParam);
        this.redirectUrl.set(this.authService.validateRedirectUrl(redirectParam));
        if (tenantIdParam) {
          this.fetchTenantName(tenantIdParam);
        } else {
          this.tenantName.set(null);
          this.loadingTenant.set(false);
        }
      });
  }

  toggleAccountCreation() {
    this.setAccountCreationMode(!this.isCreatingAccount());
  }

  private setAccountCreationMode(createAccount: boolean) {
    this.isCreatingAccount.set(createAccount);
    const confirmControl = this.form.controls.confirmPassword;
    if (createAccount) {
      confirmControl.setValue('');
      confirmControl.setErrors(null);
      confirmControl.enable();
      confirmControl.setValidators([Validators.required]);
      confirmControl.markAsPristine();
      confirmControl.markAsUntouched();
    } else {
      confirmControl.setValue('');
      confirmControl.setErrors(null);
      confirmControl.markAsPristine();
      confirmControl.markAsUntouched();
      confirmControl.disable();
      confirmControl.clearValidators();
    }
    confirmControl.updateValueAndValidity();
    this.errorState.set(null);
  }

  private fetchTenantName(tenantId: string) {
    this.loadingTenant.set(true);
    this.tenantInfoService
      .getTenantName(tenantId)
      .pipe(finalize(() => this.loadingTenant.set(false)))
      .subscribe((name) => this.tenantName.set(name));
  }

  private applyLanguage(requested: string | null) {
    const resolved = this.resolveLanguage(requested);
    if (this.currentLanguage() === resolved) {
      return;
    }
    this.currentLanguage.set(resolved);
    if (resolved === this.defaultLanguage) {
      this.translations.set(this.defaultTranslations());
      return;
    }
    this.translations.set(this.defaultTranslations());
    this.loadLanguage(resolved);
  }

  private resolveLanguage(preferred: string | null): string {
    const fromPreferred = this.normalizeLanguage(preferred);
    if (fromPreferred) {
      return fromPreferred;
    }
    const fromBrowser = this.normalizeLanguage(this.getBrowserLanguage());
    if (fromBrowser) {
      return fromBrowser;
    }
    return this.defaultLanguage;
  }

  private normalizeLanguage(value: string | null): string | null {
    if (!value) {
      return null;
    }
    const shortCode = value.toLowerCase().split('-')[0];
    return this.supportedLanguages.has(shortCode) ? shortCode : null;
  }

  private getBrowserLanguage(): string | null {
    if (typeof navigator === 'undefined') {
      return null;
    }
    return navigator.language ?? navigator.languages?.[0] ?? null;
  }

  private loadLanguage(language: string) {
    this.http
      .get<Record<string, unknown>>(`assets/i18n/${language}.json`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          const flattened = flattenTranslationsObject(data);
          this.translations.set(flattened);
        },
        error: (error) => {
          console.error(`Failed to load translations for language ${language}`, error);
          this.translations.set(this.defaultTranslations());
        }
      });
  }

  t(key: string, params: Record<string, string | number> = {}): string {
    const fallback = this.defaultTranslations();
    const active = this.translations();
    const template = active[key] ?? fallback[key] ?? key;
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => {
      const replacement = params[token];
      return replacement !== undefined ? String(replacement) : match;
    });
  }

  onSubmitEmailPassword() {
    const creatingAccount = this.isCreatingAccount();
    const { email, password, confirmPassword } = this.form.getRawValue();
    const confirmControl = this.form.controls.confirmPassword;
    if (creatingAccount && password !== confirmPassword) {
      confirmControl.setErrors({ ...(confirmControl.errors ?? {}), mismatch: true });
      confirmControl.markAsTouched();
    } else if (creatingAccount && confirmControl.hasError('mismatch')) {
      const { mismatch, ...otherErrors } = confirmControl.errors ?? {};
      const remainingErrors = Object.keys(otherErrors).length ? otherErrors : null;
      confirmControl.setErrors(remainingErrors);
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.errorState.set(null);
    this.submitInFlight.set(true);
    const emailPassword$ = creatingAccount
      ? from(this.authService.createAccountWithEmailPassword(email, password))
      : from(this.authService.signInWithEmailPassword(email, password));
    emailPassword$
      .pipe(finalize(() => this.submitInFlight.set(false)))
      .subscribe({
        next: (credential) => this.handleSuccess(credential),
        error: (error) => this.handleError(error, creatingAccount ? 'signUp' : 'signIn')
      });
  }

  signInWithGoogle() {
    this.triggerProviderSignIn(() => this.authService.signInWithGoogle());
  }

  signInWithFacebook() {
    this.triggerProviderSignIn(() => this.authService.signInWithFacebook());
  }

  signInWithApple() {
    this.triggerProviderSignIn(() => this.authService.signInWithApple());
  }

  private triggerProviderSignIn(fn: () => Promise<UserCredential>) {
    this.errorState.set(null);
    this.submitInFlight.set(true);
    from(fn())
      .pipe(finalize(() => this.submitInFlight.set(false)))
      .subscribe({
        next: (credential) => this.handleSuccess(credential),
        error: (error) => this.handleError(error)
      });
  }

  private async handleSuccess(credential: UserCredential) {
    try {
      await this.authService.finalizeSignIn(credential, this.redirectUrl(), this.state());
    } catch (error) {
      this.handleError(error);
    }
  }

  private handleError(error: unknown, context: 'signIn' | 'signUp' = 'signIn') {
    console.error('Authentication failed', error);
    const message = this.resolveFirebaseError(error, context);
    this.errorState.set(message);
  }

  private resolveFirebaseError(
    error: unknown,
    context: 'signIn' | 'signUp'
  ): { type: 'key'; key: string } | { type: 'message'; message: string } {
    const genericKey = context === 'signUp' ? 'errors.firebase.genericSignUp' : 'errors.firebase.genericSignIn';
    if (typeof error === 'string') {
      return { type: 'message', message: error };
    }
    if (error && typeof error === 'object' && 'code' in error) {
      const code = String((error as { code?: string }).code ?? '');
      switch (code) {
        case 'auth/invalid-email':
          return { type: 'key', key: 'errors.firebase.invalidEmail' };
        case 'auth/user-disabled':
          return { type: 'key', key: 'errors.firebase.userDisabled' };
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          return { type: 'key', key: 'errors.firebase.invalidCredentials' };
        case 'auth/email-already-in-use':
          return { type: 'key', key: 'errors.firebase.emailAlreadyInUse' };
        case 'auth/weak-password':
          return { type: 'key', key: 'errors.firebase.weakPassword' };
        case 'auth/popup-closed-by-user':
          return { type: 'key', key: 'errors.firebase.popupClosed' };
        case 'auth/cancelled-popup-request':
          return { type: 'key', key: 'errors.firebase.popupCancelled' };
        case 'auth/popup-blocked':
          return { type: 'key', key: 'errors.firebase.popupBlocked' };
        default:
          return { type: 'key', key: genericKey };
      }
    }
    return { type: 'key', key: genericKey };
  }
}
